import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {motion} from 'framer-motion'
import {Button} from 'primereact/button'
import {ContextMenu} from 'primereact/contextmenu'
import {Sidebar} from 'primereact/sidebar'
import React, {useCallback, useEffect, useMemo, useRef} from 'react'
import Sheet from 'react-modal-sheet'
import {useHistory} from 'react-router-dom/cjs/react-router-dom.min'
import {ReactComponent as CheckAll} from '../../assets/icons/CheckAll.svg'
import {ReactComponent as CheckIcon} from '../../assets/icons/CheckIcon.svg'
import {ReactComponent as MdSick} from '../../assets/icons/MdSick.svg'
import {ReactComponent as AssignHCP} from '../../assets/menu/AssignHCP.svg'
import {ReactComponent as AssignStaticList} from '../../assets/menu/AssignStaticList.svg'
import {getLanguageCode} from '../../i18n/LanguageUtils'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {permissionOptionKeys} from '../role/RoleConstant'
import {
    assignmentEventTypes,
    onToggleAssignmentFormListener,
} from './broadcast/AssignmentEvent'
import {
    onTableRowSelectionChangeListener,
    tableRowSelectionEventTypes,
} from './broadcast/RowEvent'
import {
    getTableToolBarEventKey,
    tableToolBarEvent,
    tableToolBarEventTypes,
} from './broadcast/TableToolBarEvent'
import {tableToolbarType} from './Constant'
import {useDatatableContext} from './DataTableContext'
import StyledAdvancedFilterExplaination from './StyledAdvancedFilterExplaination'
import StyledAssignmentToolBar from './StyledAssignmentToolBar'
import StyledDataTable from './StyledDataTable'
import StyledFilterToolBar from './StyledFilterToolBar'
import StyledSearchBox from './StyledSearchBox'
import StyledTableControlToolBar from './StyledTableControlToolBar'
import './styles.css'

// Table Variable Definition
// TableType: Used to determine that the table is presenting which type of data for the management (eg: Patient Management, Hospital Management, etc.)

const StyledDataTableWrapper = ({
    headerOptions = {
        title: 'Patient Management',
        subTitle:
            'The Patient data table displays detailed information about patients, including their full names, ID numbers, and medical details.',
    },
}) => {
    useSignals()

    //#region Hooks
    const history = useHistory()
    const {translateDatatable} = TranslateUtils()
    //#endregion

    //#region Table Context
    const {
        tableType,
        dataKey,
        queryData,
        tableToolbar,
        hideCreateButton,
        onDetailRowHovered,
        onDetailRowClick,
        handleCheckPermissionByKey,
        handleAddNew,
    } = useDatatableContext()
    //#endregion

    //#region Sheet
    const visibleSheet = useSignal(false)
    //#endregion

    //#region Table Toolbar
    const actionButtons = useMemo(() => {
        return (
            !hideCreateButton && (
                <div>
                    <h6 className="text-gray-500 font-bold m-0 mb-3 ">
                        {translateDatatable('Create')}
                    </h6>
                    <Button
                        label={translateDatatable('ADD NEW')}
                        icon="pi pi-plus"
                        className={`${
                            getLanguageCode() === 'bg' ? 'w-13rem' : 'w-10rem'
                        } `}
                        onClick={() => {
                            if (handleAddNew) {
                                handleAddNew()
                            }
                        }}
                    />
                </div>
            )
        )
    }, [hideCreateButton, translateDatatable])

    const tableToolBarItems = () => {
        return (
            <React.Fragment>
                {tableToolbar && tableToolbar?.length > 0 ? (
                    <>
                        {tableToolbar?.includes(
                            tableToolbarType.assignmentToolbar,
                        ) && <StyledAssignmentToolBar />}
                        {tableToolbar?.includes(
                            tableToolbarType.filterToolbar,
                        ) && <StyledFilterToolBar />}
                        {tableToolbar?.includes(
                            tableToolbarType.tableControlToolbar,
                        ) && <StyledTableControlToolBar />}
                    </>
                ) : null}
            </React.Fragment>
        )
    }

    //#region Header for the DataTable
    const headerTemplate = useMemo(() => {
        if (!headerOptions || Object.keys(headerOptions).length < 1) {
            return null
        }

        return (
            <React.Fragment>
                <div className="flex gap-2 justify-content-between mb-3">
                    {/* Header */}
                    <div className="flex flex-column gap-2">
                        {headerOptions?.title && (
                            <h3 className="pt-2 mb-0 text-darkcyan font-bold text-3xl md:text-4xl ">
                                {translateDatatable(headerOptions.title)}
                            </h3>
                        )}
                        {headerOptions?.subTitle && (
                            <i
                                className="underline text-700 text-md font-normal "
                                // style={{width: '40rem'}}
                            >
                                {translateDatatable(headerOptions.subTitle)}
                            </i>
                        )}
                    </div>

                    <StyledAdvancedFilterExplaination />
                </div>

                <div className="p-0 flex align-items-center justify-content-between gap-2 mb-3">
                    {/* Global Search Box */}

                    <div className="hidden sm:block">
                        <StyledSearchBox />
                    </div>
                    {/* Table Toolbar */}
                    <div className="custom-grid gap-3 justify-content-end">
                        <div className="toolbar-wrapper-xlarge">
                            {tableToolBarItems()}
                        </div>
                        <div className="toolbar-wrapper-large">
                            <div>
                                <h6 className="text-gray-500 font-bold m-0 mb-3 ">
                                    {translateDatatable('Table Controls')}
                                </h6>
                                <Button
                                    style={{
                                        background: '#f9f9f9',
                                        color: 'darkcyan',
                                    }}
                                    className="px-5"
                                    label="TOOLBAR"
                                    icon="pi pi-cog"
                                    onClick={() => {
                                        visibleSheet.value = true
                                        blockOverflownScroll()

                                        const sidebar =
                                            document.querySelector(
                                                '.sidebar-toolbar',
                                            )
                                        if (!!sidebar) {
                                            sidebar.classList.remove('hidden')

                                            const sidebarMask =
                                                document.querySelector(
                                                    '.p-sidebar-mask',
                                                )
                                            sidebarMask.classList.remove(
                                                'hidden',
                                            )
                                        }

                                        const sheet =
                                            document.querySelector(
                                                '.sheet-toolbar',
                                            )

                                        if (!!sheet) {
                                            sheet.classList.remove('hidden')
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        <div>{actionButtons}</div>
                    </div>
                </div>
                <div className="block sm:hidden">
                    <StyledSearchBox />
                </div>
            </React.Fragment>
        )
    }, [headerOptions])

    //#region Columns for the DataTable
    const viewedColumnsRef = useRef(null)
    //#endregion

    //#region Table Row Selection
    const selectedRows = useSignal([])
    const shouldDisableRowSelection = useSignal(false)

    const handleChangeRowSelection = useCallback(
        e => {
            if (e.type === 'checkbox' || e.type === 'all') {
                selectedRows.value = e.value

                shouldDisableRowSelection.value = e.value.length > 0

                onTableRowSelectionChangeListener(
                    tableType,
                    tableRowSelectionEventTypes.ROW_SELECTION_CHANGE,
                    e.value,
                )
            }
        },
        [selectedRows.value, shouldDisableRowSelection.value],
    )

    const handleOnRowSelect = useCallback(
        e => {
            if (e.type === 'row') {
                if (selectedRows.value?.length > 0) {
                    return
                }

                if (onDetailRowClick && !!e?.data && !!e?.data?.id) {
                    onDetailRowClick(e.data?.id)
                }
            }
        },
        [selectedRows.value],
    )

    const handleOnRowUnselect = useCallback(e => {}, [])

    const handleOnRowHovered = useCallback(
        rowData => {
            if (!rowData || !rowData?.data || !rowData?.data[dataKey]) {
                return
            }

            if (selectedRows.value?.length > 0) {
                return
            }

            if (onDetailRowHovered) {
                onDetailRowHovered(rowData.data[dataKey])
            }
        },
        [selectedRows.value],
    )
    //#endregion

    //#region Context Menu
    const selectedContextMenuItem = useSignal(null)
    const rowSelectedContextMenu = useRef(null)
    const rowUnselectedContextMenu = useRef(null)

    const handleExcludeRow = () => {
        if (!selectedContextMenuItem.value || selectedRows.value?.length < 1) {
            return
        }

        const rowsFiltered = selectedRows.value?.filter(
            x =>
                x[dataKey] &&
                selectedContextMenuItem.value[dataKey] &&
                x[dataKey] !== selectedContextMenuItem.value[dataKey],
        )

        selectedRows.value = rowsFiltered
        selectedContextMenuItem.value = null

        if (selectedRows.peek().length === 0) {
            shouldDisableRowSelection.value = false
        }

        onTableRowSelectionChangeListener(
            tableType,
            tableRowSelectionEventTypes.ROW_SELECTION_CHANGE,
            selectedRows.peek(),
        )
    }

    const handleExcludeAllRows = () => {
        selectedRows.value = []
        selectedContextMenuItem.value = null
        shouldDisableRowSelection.value = false

        onTableRowSelectionChangeListener(
            tableType,
            tableRowSelectionEventTypes.ROW_SELECTION_CHANGE,
            selectedRows.peek(),
        )
    }

    const handleIncludeRow = () => {
        if (!selectedContextMenuItem.value) {
            return
        }

        selectedRows.value = [
            ...selectedRows.value,
            selectedContextMenuItem.value,
        ]
        selectedContextMenuItem.value = null

        onTableRowSelectionChangeListener(
            tableType,
            tableRowSelectionEventTypes.ROW_SELECTION_CHANGE,
            selectedRows.peek(),
        )
    }

    const handleIncludeAllRows = () => {
        selectedRows.value =
            !!queryData && isArrayAndNotEmpty(queryData.content)
                ? queryData.content
                : []
        selectedContextMenuItem.value = null

        onTableRowSelectionChangeListener(
            tableType,
            tableRowSelectionEventTypes.ROW_SELECTION_CHANGE,
            selectedRows.peek(),
        )
    }

    const handleViewInformation = () => {
        if (
            !selectedContextMenuItem.value ||
            !selectedContextMenuItem.value.id ||
            !onDetailRowClick
        ) {
            return
        }

        onDetailRowClick(selectedContextMenuItem.value.id)
    }

    const assignmentSelectedContextMenuItems = useMemo(() => {
        if (
            !tableToolbar ||
            !tableToolbar?.includes(tableToolbarType.assignmentToolbar)
        ) {
            return []
        }

        return [
            {
                separator: true,
            },
            {
                label: (
                    <div>
                        <h6 className="text-gray-500 font-bold m-0 mb-1 section-header">
                            {translateDatatable('Assignment')}
                        </h6>
                        <small className="text-gray-500 section-header">
                            {translateDatatable('Assign these patients to:')}
                        </small>
                    </div>
                ),
                visible:
                    (tableToolbar &&
                        tableToolbar?.length > 0 &&
                        tableToolbar.includes(
                            tableToolbarType.assignmentToolbar,
                        )) ||
                    (handleCheckPermissionByKey(
                        permissionOptionKeys.allowToAssignPathway,
                    ) &&
                        handleCheckPermissionByKey(
                            permissionOptionKeys.allowToAssignStaticList,
                        ) &&
                        handleCheckPermissionByKey(
                            permissionOptionKeys.allowToAssignHcp,
                        )),
            },
            {
                label: 'Static List',
                icon: (
                    <span className="p-menuitem-icon pi pi-fw">
                        <AssignStaticList />
                    </span>
                ),
                visible:
                    (tableToolbar &&
                        tableToolbar?.length > 0 &&
                        tableToolbar.includes(
                            tableToolbarType.assignmentToolbar,
                        )) ||
                    handleCheckPermissionByKey(
                        permissionOptionKeys.allowToAssignStaticList,
                    ),
                command: () =>
                    onToggleAssignmentFormListener(
                        tableType,
                        assignmentEventTypes.ON_TOGGLE_STATIC_LIST,
                    ),
            },
            {
                label: 'Pathway',
                icon: (
                    <span className="p-menuitem-icon pi pi-fw">
                        <MdSick />
                    </span>
                ),
                visible:
                    (tableToolbar &&
                        tableToolbar?.length > 0 &&
                        tableToolbar.includes(
                            tableToolbarType.assignmentToolbar,
                        )) ||
                    handleCheckPermissionByKey(
                        permissionOptionKeys.allowToAssignPathway,
                    ),
                command: () =>
                    onToggleAssignmentFormListener(
                        tableType,
                        assignmentEventTypes.ON_TOGGLE_PATHWAY,
                    ),
            },
            {
                label: 'HC.Practitioner',
                icon: (
                    <span className="p-menuitem-icon pi pi-fw">
                        <AssignHCP />
                    </span>
                ),
                visible:
                    (tableToolbar &&
                        tableToolbar?.length > 0 &&
                        tableToolbar.includes(
                            tableToolbarType.assignmentToolbar,
                        )) ||
                    handleCheckPermissionByKey(
                        permissionOptionKeys.allowToAssignHcp,
                    ),
                command: () =>
                    onToggleAssignmentFormListener(
                        tableType,
                        assignmentEventTypes.ON_TOGGLE_HCP,
                    ),
            },
        ]
    }, [tableToolbar])

    const selectedContextMenuModel = [
        {
            label: (
                <h6 className="text-gray-500 font-bold m-0 mb-1 section-header">
                    {translateDatatable('Total records')}
                </h6>
            ),
        },
        {
            label: (
                <span className="font-bold">
                    {selectedRows.value.length} record(s)
                </span>
            ),
            icon: 'pi pi-fw pi-users',
            command: () => {},
        },
        {
            separator: true,
        },
        {
            label: (
                <h6 className="text-gray-500 font-bold m-0 mb-1 section-header">
                    {translateDatatable('Actions')}
                </h6>
            ),
        },
        {
            label: 'Exclude this row',
            icon: 'pi pi-fw pi-minus',
            command: handleExcludeRow,
        },
        {
            label: 'Exclude all rows',
            icon: 'pi pi-fw pi-minus-circle',
            command: handleExcludeAllRows,
        },

        ...assignmentSelectedContextMenuItems,

        {
            separator: true,
        },
        {
            label: (
                <h6 className="text-gray-500 font-bold m-0 mb-1 section-header">
                    {translateDatatable('Visibilily')}
                </h6>
            ),
        },
        {
            label: 'Close this menu',
            icon: 'pi pi-fw pi-times-circle',
            command: () => (selectedContextMenuItem.value = null),
        },
    ]

    const unselectedContextMenuModel = [
        {
            label: (
                <h6 className="text-gray-500 font-bold m-0 mb-1 section-header">
                    {translateDatatable('Actions')}
                </h6>
            ),
        },
        {
            label: 'View Information',
            icon: (
                <span className="p-menuitem-icon cursor-pointer">
                    <i className="pi pi-eye"></i>
                </span>
            ),
            command: handleViewInformation,
        },
        {
            separator: true,
        },
        {
            label: 'Select this row',
            icon: (
                <span className="p-menuitem-icon pi pi-fw">
                    <CheckIcon />
                </span>
            ),
            command: handleIncludeRow,
        },
        {
            label: 'Select all rows',
            icon: (
                <span className="p-menuitem-icon pi pi-fw">
                    <CheckAll />
                </span>
            ),
            command: handleIncludeAllRows,
        },
        {
            separator: true,
        },
        {
            label: (
                <h6 className="text-gray-500 font-bold m-0 mb-1 section-header">
                    {translateDatatable('Visibilily')}
                </h6>
            ),
        },
        {
            label: 'Close this menu',
            icon: 'pi pi-fw pi-times-circle',
            command: () => (selectedContextMenuItem.value = null),
        },
    ]

    // Show Context Menu
    const handleOnContextMenu = event => {
        const isSelected = selectedRows.value.some(
            x =>
                x[dataKey] &&
                selectedContextMenuItem.value[dataKey] &&
                x[dataKey] === selectedContextMenuItem.value[dataKey],
        )

        if (isSelected) {
            rowSelectedContextMenu.current.show(event?.originalEvent)
            rowUnselectedContextMenu.current.hide(event?.originalEvent)
        } else {
            rowUnselectedContextMenu.current.show(event?.originalEvent)
            rowSelectedContextMenu.current.hide(event?.originalEvent)
        }
    }

    // Context Menu Selection Change
    const handleOnContextMenuSelectionChange = event => {
        selectedContextMenuItem.value = event.value
    }

    // Clear Selected Context Menu Item
    const clearSelectedContextItem = () => {
        selectedContextMenuItem.value = null
    }
    //#endregion

    //#endregion Window Resize
    useEffect(() => {
        window.addEventListener('resize', e => {
            if (window.innerWidth > 1200) {
                visibleSheet.value = false
                unblockOverflownScroll()
            }

            if (window.innerWidth < 768) {
                const sidebar = document.querySelector('.sidebar-toolbar')
                if (!!sidebar) {
                    sidebar.classList.add('hidden')

                    const sidebarMask =
                        document.querySelector('.p-sidebar-mask')
                    sidebarMask.classList.add('hidden')
                }

                const sheet = document.querySelector('.sheet-toolbar')
                if (!!sheet) {
                    sheet.classList.remove('hidden')
                }
            }

            if (window.innerWidth >= 768 && window.innerWidth <= 1200) {
                const sheet = document.querySelector('.sheet-toolbar')
                if (!!sheet) {
                    sheet.classList.add('hidden')
                }

                const sidebar = document.querySelector('.sidebar-toolbar')
                if (!!sidebar) {
                    sidebar.classList.remove('hidden')

                    const sidebarMask =
                        document.querySelector('.p-sidebar-mask')
                    sidebarMask.classList.remove('hidden')
                }
            }
        })

        return () => {
            window.removeEventListener('resize', () => {})

            if (visibleSheet.value) {
                visibleSheet.value = false
                unblockOverflownScroll()
            }
        }
    }, [])
    //#endregion

    //#region Sidebar or Sheet for the Toolbar
    useEffect(() => {
        tableToolBarEvent.on(
            `${getTableToolBarEventKey(
                tableType,
                tableToolBarEventTypes.ON_ITEM_SELECTED,
            )}`,
            () => {
                const sidebar = document.querySelector('.sidebar-toolbar')
                if (!!sidebar) {
                    sidebar.classList.add('hidden')

                    const sidebarMask =
                        document.querySelector('.p-sidebar-mask')
                    sidebarMask.classList.add('hidden')
                }

                const sheet = document.querySelector('.sheet-toolbar')
                if (!!sheet) {
                    sheet.classList.add('hidden')
                }

                unblockOverflownScroll()
            },
        )

        return () => {
            tableToolBarEvent.removeListener(
                tableType,
                tableToolBarEventTypes.ON_ITEM_SELECTED,
            )
        }
    }, [])
    //#endregion

    return (
        <React.Fragment>
            <StyledDataTable
                header={headerTemplate}
                selection={selectedRows.value}
                selectionMode={
                    shouldDisableRowSelection.value ? 'checkbox' : 'single'
                }
                className="styled-table-min-h-fullscreen"
                onSelectionChange={handleChangeRowSelection}
                onRowSelect={handleOnRowSelect}
                onRowUnselect={handleOnRowUnselect}
                onRowMouseEnter={handleOnRowHovered}
                contextMenu={{
                    onContextMenu: handleOnContextMenu,
                    contextMenuSelection: selectedContextMenuItem.value,
                    onContextMenuSelectionChange:
                        handleOnContextMenuSelectionChange,
                    clearSelectedContextItem: clearSelectedContextItem,
                }}
            />

            <ContextMenu
                model={selectedContextMenuModel}
                ref={rowSelectedContextMenu}
                onHide={() => {}}
            />

            <ContextMenu
                model={unselectedContextMenuModel}
                ref={rowUnselectedContextMenu}
                onHide={() => {}}
            />

            <Sheet
                className="sheet-toolbar"
                style={{zIndex: 2105}}
                isOpen={visibleSheet.value}
                // detent="content-height"
                disableScrollLocking={true}
                onOpenStart={() => {
                    // const sidebar = document.querySelector('.sidebar-toolbar')
                    // if (!!sidebar) {
                    //     sidebar.classList.add('hidden')
                    // }

                    onTableRowSelectionChangeListener(
                        tableType,
                        tableRowSelectionEventTypes.ROW_SELECTION_CHANGE,
                        selectedRows.value,
                    )
                }}
                onClose={() => {
                    visibleSheet.value = false
                    unblockOverflownScroll()
                }}>
                <Sheet.Container>
                    <Sheet.Header />
                    <Sheet.Content
                        className="col-12 px-3"
                        style={{height: 'calc(100vh-20rem)'}}>
                        <div className="py-2 px-1 overflow-y-auto">
                            {/* Header */}
                            <div className="flex align-items-center">
                                <div className="mr-2">
                                    <i
                                        className="pi pi-cog"
                                        style={{fontSize: '26px'}}></i>
                                </div>
                                <div>
                                    <h3 className="font-bold m-0">Toolbar</h3>
                                </div>
                            </div>
                            <div>
                                <p>
                                    The toolbar contains all the{' '}
                                    <span className="text-darkcyan">
                                        necessary actions
                                    </span>{' '}
                                    that you can perform on the table.
                                </p>
                            </div>
                            <hr />
                            {/* Content */}
                            <Sheet.Scroller>
                                {tableToolBarItems()}
                            </Sheet.Scroller>
                        </div>
                    </Sheet.Content>
                </Sheet.Container>
                {/* <Sheet.Backdrop /> */}
            </Sheet>
            <Sidebar
                id="sidebar-toolbar-datatable"
                className="sidebar-toolbar w-30rem"
                position="right"
                visible={visibleSheet.value}
                showCloseIcon={true}
                blockScroll
                baseZIndex={2100}
                onShow={() => {
                    onTableRowSelectionChangeListener(
                        tableType,
                        tableRowSelectionEventTypes.ROW_SELECTION_CHANGE,
                        selectedRows.value,
                    )
                }}
                onHide={() => {
                    visibleSheet.value = false
                    unblockOverflownScroll()
                }}>
                <motion.div
                    layout
                    transition={{layout: {duration: 0.15}}}
                    className="pt-3">
                    <div className="py-2 px-1">
                        {/* Header */}
                        <div className="flex align-items-center">
                            <div className="mr-2">
                                <i
                                    className="pi pi-cog"
                                    style={{fontSize: '20px'}}></i>
                            </div>
                            <div>
                                <h3 className="font-bold m-0">Toolbar</h3>
                            </div>
                        </div>
                        <div>
                            <p>
                                The toolbar contains all the{' '}
                                <span className="text-darkcyan">
                                    necessary actions
                                </span>{' '}
                                that you can perform on the table.
                            </p>
                        </div>
                        <hr />
                        {/* Content */}
                        {tableToolBarItems()}
                    </div>
                </motion.div>
            </Sidebar>
        </React.Fragment>
    )
}

export default StyledDataTableWrapper
